import { FC, useMemo } from 'react';
import { HStack } from '@chakra-ui/react';

import {
  TeamMember as TeamMemberGQL,
  TeamMemberRole,
  TeamMemberStatus,
} from '__generated__/GQL';
import Chip from 'components/_core/Chip';
import useTeamMembersInvitationQuota from 'features/Team/hooks/useTeamMembersInvitationQuota';
import { MembersTab } from 'features/Team/routes';

import { hasRole } from '../../../../utils';
import {
  filterToTab,
  RoleWithoutAccountHolder,
  tabToFilter,
} from '../../types';
import locales from './locales';

type RoleCount = {
  [TeamMemberRole.Accountant]: number;
  [TeamMemberRole.Admin]: number;
  [TeamMemberRole.Employee]: number;
  [TeamMemberStatus.Locked]: number;
};

type TeamMember = Pick<TeamMemberGQL, 'roles' | 'status'>;

interface FilterBarProps {
  selectedTab: MembersTab | null;
  onSelectTab: (role: MembersTab | null) => void;
  members: TeamMember[];
  isOnlyAccountHolder: boolean;
  isAccountantsOnly: boolean;
}

const initialTotalCount: RoleCount = {
  [TeamMemberRole.Accountant]: 0,
  [TeamMemberRole.Admin]: 0,
  [TeamMemberRole.Employee]: 0,
  [TeamMemberStatus.Locked]: 0,
};

const FilterBar: FC<FilterBarProps> = ({
  isAccountantsOnly,
  isOnlyAccountHolder,
  members,
  onSelectTab,
  selectedTab,
}) => {
  const totalCount: RoleCount = useMemo(
    () =>
      members.reduce((acc, member) => {
        if (member.status === TeamMemberStatus.Locked) {
          if (
            isAccountantsOnly &&
            !hasRole(member, TeamMemberRole.Accountant)
          ) {
            return acc;
          }

          return {
            ...acc,
            [member.status]: acc[member.status as RoleWithoutAccountHolder] + 1,
          };
        }
        if (member.roles) {
          return member.roles.reduce(
            (count, role) => ({
              ...count,
              [role]: acc[role as RoleWithoutAccountHolder] + 1,
            }),
            acc,
          );
        }

        return acc;
      }, initialTotalCount),
    [isAccountantsOnly, members],
  );

  const memberFilterOptions: RoleWithoutAccountHolder[] = [
    TeamMemberRole.Admin,
    TeamMemberRole.Employee,
    TeamMemberRole.Accountant,
    TeamMemberStatus.Locked,
  ];

  const accountantFilterOptions: RoleWithoutAccountHolder[] = [
    TeamMemberRole.Accountant,
    TeamMemberStatus.Locked,
  ];

  const memberFilters: RoleWithoutAccountHolder[] = isAccountantsOnly
    ? accountantFilterOptions
    : memberFilterOptions;

  const filters = memberFilters.map((filterName) => ({
    filterName,
    isActive: tabToFilter[selectedTab ?? 'all'] === filterName,
    label: locales.filters[filterName],
    onClick: () => onSelectTab(filterToTab[filterName] ?? null),
  }));

  const {
    teamMemberInvitationsQuota: { limit: invitationsLimit },
  } = useTeamMembersInvitationQuota();
  const canInviteAdmins = invitationsLimit > 1;

  return (
    <HStack spacing="space-16">
      <Chip isActive={!selectedTab} onClick={() => onSelectTab(null)}>
        {locales.filters.all}
        {!isOnlyAccountHolder
          ? ` (${
              members.filter(
                (member) => member.status !== TeamMemberStatus.Locked,
              ).length
            })`
          : null}
      </Chip>

      {filters.map(({ filterName, isActive, label, onClick }) => {
        const isAdminFilter = filterName === TeamMemberRole.Admin;
        const isLockedFilter = filterName === TeamMemberStatus.Locked;
        const hasNoMembers = totalCount[filterName] === 0;
        const shouldHide =
          (isAdminFilter && !canInviteAdmins) ||
          (isLockedFilter && hasNoMembers);

        if (shouldHide) {
          return null;
        }

        return (
          <Chip
            isActive={isActive}
            isDisabled={isOnlyAccountHolder}
            key={label}
            onClick={onClick}
          >
            {label}
            {!isOnlyAccountHolder && filterName !== TeamMemberStatus.Locked
              ? ` (${totalCount[filterName]})`
              : null}
          </Chip>
        );
      })}
    </HStack>
  );
};

export default FilterBar;
